import React, { useCallback, useEffect, useState } from "react";

import { Button, Spinner } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useQueryClient } from "@tanstack/react-query";

import SubscriptionService from "@/services/SubscriptionService";

import { RETURN_TO_KEY } from "@/components/organisms/UpgradeModal/UpgradeModal";
import { PageLayout } from "@/components/templates";

import { subscriptionKeys } from "@/constants/CQueryKeys";

import { TSubscriptionState } from "@/types/TSubscription";

/** How many times, and how far apart, the page asks before giving up on a live answer. */
export const SUCCESS_POLL = { attempts: 5, intervalMs: 2_000 };

/**
 * The page the upgrade started from, read once and forgotten: the next
 * checkout must not land on a page from a previous one.
 */
export const takeReturnTo = (): string | null => {
	try {
		const path = sessionStorage.getItem(RETURN_TO_KEY);
		sessionStorage.removeItem(RETURN_TO_KEY);
		return path && path !== "/subscription/success" ? path : null;
	} catch {
		return null;
	}
};

/**
 * Where Stripe sends the browser back after a paid Checkout. The
 * webhook that activates the plan may land after this page does, so it
 * asks the API a few times rather than trusting the URL: only a
 * subscription Stripe confirmed counts. Three states — checking,
 * confirmed, taking longer — and never an error: the webhook is the
 * truth, and it is on its way.
 */
export const SubscriptionSuccess: React.FC = () => {
	const [state, setState] = useState<TSubscriptionState | null>(null);
	const [gaveUp, setGaveUp] = useState(false);
	const [round, setRound] = useState(0);
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	useEffect(() => {
		let cancelled = false;
		let attempt = 0;
		setGaveUp(false);

		const poll = async () => {
			while (!cancelled && attempt < SUCCESS_POLL.attempts) {
				attempt += 1;
				try {
					const fresh = await SubscriptionService.fetch();
					if (cancelled) return;
					queryClient.setQueryData(subscriptionKeys.current, fresh);
					if (
						fresh.subscription?.hasCustomer &&
						fresh.plan.code !== "free"
					) {
						setState(fresh);
						toast.success(
							t("subscription.success.toast", {
								plan: fresh.plan.name,
							})
						);
						return;
					}
				} catch {
					// A transient failure: the next attempt may do.
				}
				await new Promise((resolve) =>
					setTimeout(resolve, SUCCESS_POLL.intervalMs)
				);
			}
			if (!cancelled) setGaveUp(true);
		};

		void poll();
		return () => {
			cancelled = true;
		};
	}, [queryClient, round]);

	const resume = useCallback(() => {
		navigate(takeReturnTo() ?? "/");
	}, [navigate]);

	return (
		<PageLayout title={t("subscription.success.title")}>
			<div className="flex flex-col items-center gap-6 py-12 text-center">
				{state ? (
					<>
						<p
							className="text-lg"
							data-testid="subscription-success-done"
						>
							{t("subscription.success.done", {
								plan: state.plan.name,
							})}
						</p>
						<Button color="primary" onClick={resume}>
							{t("subscription.success.resume")}
						</Button>
					</>
				) : gaveUp ? (
					<>
						<p
							className="max-w-md text-neutral-500"
							data-testid="subscription-success-slow"
						>
							{t("subscription.success.slow")}
						</p>
						<div className="flex gap-2">
							<Button
								variant="outline"
								onClick={() => setRound((n) => n + 1)}
							>
								{t("subscription.success.retry")}
							</Button>
							<Button
								variant="ghost"
								color="neutral"
								onClick={() => navigate("/subscription")}
							>
								{t("subscription.success.back")}
							</Button>
						</div>
					</>
				) : (
					<>
						<Spinner />
						<p className="text-neutral-500">
							{t("subscription.success.pending")}
						</p>
					</>
				)}
			</div>
		</PageLayout>
	);
};
